import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { klona } from 'klona';
import { TrackerCustomEvent, useTracker } from 'common-package';
import { SlideSelect } from '@/components';
import service from '@/service';
import { ZoomParams } from '../..';
import { getOption } from './echartsOption';

import cx from './index.module.scss';

const TIME_DIMENSION_OPTIONS = [
  { value: 'month', label: '月' },
  { value: 'week', label: '周' },
  { value: 'day', label: '日' },
];

interface StatisticsInfoProps {
  className?: string;
}

const StatisticsInfo: React.FC<StatisticsInfoProps> = props => {
  const { className } = props;

  const [chartData, setChartData] = useState<ServiceOrderChartFormat>(null);

  const [zoomParams, setZoomParams] = useState<ZoomParams>({
    zoomStartValue: null,
    zoomEndValue: null,
  });

  const [chartDataByMonth, setChartDataByMonth] = useState<GetChartDateByMonthResponse[]>(null);
  const [chartDataByWeek, setChartDataByWeek] = useState<GetChartDateByWeekResponse[]>(null);
  const [chartDataByDay, setChartDataByDay] = useState<GetChartDateByDayResponse[]>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasMoreData, setHasMoreData] = useState(true);
  const chartRef = useRef(null);
  const { sendEvent } = useTracker();

  const [timeDimension, setTimeDimension] = useState<TimeDimension>('month');

  const option = useMemo(() => {
    return getOption(chartData, zoomParams);
  }, [chartData, zoomParams]);

  const getEChartsDataByMonth = useCallback(
    async (offset: number, reset: boolean, cursor?: string) => {
      try {
        reset && setIsLoading(true);
        const { data } = await service.serviceRecordApi.getChartDataByMonth({
          cursor,
          offset,
        });
        let totalData = reset ? data : [...data, ...chartDataByMonth];
        setChartDataByMonth(totalData);
        const newTotalData = klona(totalData);
        const formattedData = newTotalData?.reverse()?.reduce(
          (acc, item) => {
            acc.type = 'month';
            const formatDate = dayjs()
              .year(item.year)
              .month(item.month - 1)
              .date(1)
              .format('YYYY-MM-DD');
            acc.dateArr.push(formatDate);
            acc.totalOrdersArr.push(item.stats.totalOrders);
            acc.totalCustomersArr.push(item.stats.totalCustomers);
            acc.totalAmountArr.push(item.stats.totalAmount?.toFixed(2));
            return acc;
          },
          {
            type: null,
            dateArr: [],
            totalOrdersArr: [],
            totalCustomersArr: [],
            totalAmountArr: [],
          }
        );
        setChartData(formattedData);
        const _hasMoreData = data?.length >= Math.abs(offset);
        setHasMoreData(_hasMoreData);
      } finally {
        reset && setIsLoading(false);
      }
    },
    [chartDataByMonth]
  );

  const getEChartsDataByWeek = useCallback(
    async (offset: number, reset: boolean, cursor?: string) => {
      try {
        reset && setIsLoading(true);
        const { data } = await service.serviceRecordApi.getChartDataByWeek({
          cursor,
          offset,
        });

        let totalData = reset ? data : [...data, ...chartDataByWeek];
        setChartDataByWeek(totalData);
        const newTotalData = klona(totalData);

        const formattedData = newTotalData?.reverse()?.reduce(
          (acc, item) => {
            acc.type = 'week';
            const formatDate = item.startDate + ',' + item.endDate;
            acc.dateArr.push(formatDate);
            acc.totalOrdersArr.push(item.stats.totalOrders);
            acc.totalCustomersArr.push(item.stats.totalCustomers);
            acc.totalAmountArr.push(item.stats.totalAmount?.toFixed(2));
            return acc;
          },
          {
            type: null,
            dateArr: [],
            totalOrdersArr: [],
            totalCustomersArr: [],
            totalAmountArr: [],
          }
        );

        setChartData(formattedData);
        const _hasMoreData = data?.length >= Math.abs(offset);
        setHasMoreData(_hasMoreData);
      } finally {
        setIsLoading(false);
      }
    },
    [chartDataByWeek]
  );

  const getChartDataByDay = useCallback(
    async (offset: number, reset: boolean, cursor?: string) => {
      try {
        reset && setIsLoading(true);
        const { data } = await service.serviceRecordApi.getChartDataByDay({
          cursor,
          offset,
        });

        let totalData = reset ? data : [...data, ...chartDataByDay];
        setChartDataByDay(totalData);
        const newTotalData = klona(totalData);
        const formattedData = newTotalData.reverse()?.reduce(
          (acc, item) => {
            acc.type = 'day';
            acc.dateArr.push(item.date);
            acc.totalOrdersArr.push(item.stats.totalOrders);
            acc.totalCustomersArr.push(item.stats.totalCustomers);
            acc.totalAmountArr.push(item.stats.totalAmount?.toFixed(2));
            return acc;
          },
          {
            type: null,
            dateArr: [],
            totalOrdersArr: [],
            totalCustomersArr: [],
            totalAmountArr: [],
          }
        );
        setChartData(formattedData);
        const _hasMoreData = data?.length >= Math.abs(offset);
        setHasMoreData(_hasMoreData);
      } finally {
        reset && setIsLoading(false);
      }
    },
    [chartDataByDay]
  );

  const getEChartsData = useCallback(
    async (date: TimeDimension) => {
      setZoomParams({
        zoomStartValue: null,
        zoomEndValue: null,
      });
      if (date === 'month') {
        getEChartsDataByMonth(-4, true, '');
      } else if (date === 'week') {
        getEChartsDataByWeek(-5, true, '');
      } else if (date === 'day') {
        getChartDataByDay(-6, true, '');
      }
    },
    [getChartDataByDay, getEChartsDataByMonth, getEChartsDataByWeek]
  );

  const loadMoreData = useCallback(
    (date: TimeDimension) => {
      if (hasMoreData) {
        const lastCursor = chartData?.dateArr[chartData?.dateArr?.length - 1];
        if (date === 'month') {
          getEChartsDataByMonth(-3, false, lastCursor);
          const zoomStartValue = chartData?.dateArr[chartData?.dateArr?.length - 2];
          setZoomParams({ zoomStartValue });
        } else if (date === 'week') {
          const zoomStartValue = chartData?.dateArr[chartData?.dateArr?.length - 3];
          setZoomParams({ zoomStartValue });
          getEChartsDataByWeek(-4, false, lastCursor?.split(',')?.[0]);
        } else if (date === 'day') {
          const zoomStartValue = chartData?.dateArr[chartData?.dateArr?.length - 4];
          setZoomParams({ zoomStartValue });
          getChartDataByDay(-5, false, lastCursor);
        }
      }
    },
    [chartData?.dateArr, getChartDataByDay, getEChartsDataByMonth, getEChartsDataByWeek, hasMoreData]
  );

  const onDataZoom = useCallback(
    (param: any) => {
      if (Math.round(param?.batch?.[0]?.end) === 100 || Math.round(param?.end) === 100) {
        loadMoreData(timeDimension);
      }
    },
    [loadMoreData, timeDimension]
  );

  useEffect(() => {
    // 初始加载月维度
    getEChartsDataByMonth(-4, true, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cx('container', className)}>
      <SlideSelect
        steps={TIME_DIMENSION_OPTIONS}
        value={timeDimension}
        className={cx('slide-select')}
        onChange={value => {
          setTimeDimension(value as TimeDimension);
          getEChartsData(value as TimeDimension);
          sendEvent?.(TrackerCustomEvent.ServiceRecordByTimeDimensionClk, {
            et: 'CLK',
            c1: TIME_DIMENSION_OPTIONS?.find(item => item.value === value)?.label,
          });
        }}
      />
      {isLoading ? (
        <div className={cx('loading-wrapper')} />
      ) : (
        <div className={cx('echarts-wrapper')}>
          <ReactECharts
            ref={chartRef}
            option={option}
            style={{ height: '100%' }}
            opts={{ renderer: 'svg' }}
            onEvents={{ dataZoom: onDataZoom }}
            lazyUpdate={true}
            notMerge={false}
          />
        </div>
      )}
    </div>
  );
};

export default StatisticsInfo;
